import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import MiniHeader from "@/components/site/MiniHeader";
import { ownerApi, apiErrorText } from "@/lib/ownerApi";

export default function OwnerLoginPage() {
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [loading, setLoading] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const navigate = useNavigate();

  const forgot = async () => {
    setSendingReset(true);
    setError("");
    try {
      const { data } = await ownerApi.post("/auth/forgot-password");
      setNotice(data.message);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setSendingReset(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await ownerApi.post("/auth/login", { password });
      navigate("/admin");
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="owner-login-page" className="min-h-screen">
      <MiniHeader />
      <main className="max-w-md mx-auto px-5 py-20">
        <div className="card-surface p-8">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)]">
            <Lock size={18} className="text-[#D4AF37]" />
          </span>
          <h1 className="serif text-3xl text-[#F5F0E8] font-light mt-5">Owner sign in</h1>
          <p className="mt-2 text-sm text-[#A0988E] spectral">Sign in to see every family, send finished films and change your homepage music.</p>
          <button type="button" data-testid="owner-google-signin-button" onClick={googleSignIn}
                  className="mt-7 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#F5F0E8] text-[#1A1714] px-5 py-3 text-[15px] font-medium hover:bg-white transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6 text-[11px] uppercase tracking-widest text-[#A0988E]/70">
            <span className="h-px flex-1 bg-[rgba(197,165,90,0.2)]" /> or use your password <span className="h-px flex-1 bg-[rgba(197,165,90,0.2)]" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <input
              data-testid="owner-password-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-transparent border border-[rgba(197,165,90,0.3)] rounded-md px-4 py-3 text-[#F5F0E8] placeholder:text-[#A0988E]/60 focus:outline-none focus:border-[#D4AF37]"
            />
            {error && <p data-testid="owner-login-error" className="text-sm text-[#E6B89C]">{error}</p>}
            {notice && <p data-testid="owner-login-notice" className="text-sm text-[#D4AF37]">{notice}</p>}
            <button data-testid="owner-login-submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null} Sign in
            </button>
            <button type="button" data-testid="owner-forgot-password-button" onClick={forgot} disabled={sendingReset}
                    className="w-full text-center text-xs text-[#A0988E] hover:text-[#F5F0E8] disabled:opacity-60">
              {sendingReset ? "Sending…" : "Forgot password? Email me a reset link"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
